import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { UserChecker, UserCheckResult } from './user-checker';
import { Credentials } from './credentials';
import { AuthConfig, CurrentUserStore } from './current-user-store';
import { User } from './user';

@Injectable()
export class AuthService {
  private _loginMode = false;
  private _credentialsSubj = new ReplaySubject<Credentials>();
  private _authLogSubj = new Subject<UserCheckResult>();

  constructor(private _currentUserStore: CurrentUserStore, private _userChecker: UserChecker, private _authConfig: AuthConfig) {}

  get loginMode(): boolean {
    return this._loginMode;
  }

  set loginMode(v: boolean) {
    this._loginMode = v;
  }

  get credentialsSubj(): Subject<Credentials> {
    if (this._credentialsSubj.isStopped) {
      this._credentialsSubj = new ReplaySubject<Credentials>();
    }
    return this._credentialsSubj;
  }

  private onEnterLoginMode: () => void;
  setOnEnterLoginMode(onEnterLoginMode: () => void) {
    this.onEnterLoginMode = onEnterLoginMode;
  }

  private _successLoginResult$ = new AsyncSubject<User>();
  private get successLoginResult$(): Observable<User> {
    if (this._successLoginResult$.isStopped) {
      this._successLoginResult$ = new AsyncSubject<User>();
    }
    return this._successLoginResult$;
  }
  private login$(automaticMode: boolean, extendExpiration: boolean): Observable<User> {
    const storedUser = this._currentUserStore.get();
    if (storedUser !== null) {
      if (extendExpiration && this._authConfig.getSlidingExpirationMs() > 0) {
        // продлим действие сохраненных данных авторизации
        this._currentUserStore.store(storedUser);
      }
      return of(storedUser);
    }
    if (!this.loginMode) {
      this.loginMode = true;
      if (automaticMode && this.onEnterLoginMode) {
        this.onEnterLoginMode();
      }
      this.credentialsSubj
        .pipe(
          switchMap((c) => this._userChecker.check(c)),
          tap((checkRes) => this._authLogSubj.next(checkRes)),
          filter((checkRes) => checkRes.result),
          tap((checkRes) => this.setCurrentUser(checkRes.user)),
          tap((_) => (this.loginMode = false)),
          tap((_) => this.credentialsSubj.complete()),
          tap((checkRes) => this._successLoginResult$.next(checkRes.user)),
          tap((_) => this._successLoginResult$.complete()),
        )
        .subscribe();
    }
    return this.successLoginResult$;
  }

  getCredentials$(): Observable<Credentials> {
    return this.login$(true, true).pipe(map((_) => _.credentials));
  }

  check$(): Observable<Credentials> {
    return this.login$(true, false).pipe(map((_) => _.credentials));
  }

  get authLog$(): Observable<UserCheckResult> {
    return this._authLogSubj;
  }

  quit() {
    this._currentUserStore.clear();
    return this.login$(false, false).subscribe();
  }

  private _currentUser: User | null = null;
  get currentUser(): User | null {
    return this._currentUser;
  }
  private setCurrentUser(user: User) {
    this._currentUser = user;
    this._currentUserStore.store(user);
  }
}
